/**
 * The words the proposal generator picks from (src/lib/proposalGenerate.js).
 *
 * Studio voice: plain, first person from Efren, no em dashes. Nothing here
 * states a price, a statistic or a client's words: figures come from the
 * investment section and quotes only from what the studio has stored.
 *
 * Slots, filled by the generator:
 *   {company}  the client's company, else "you"
 *   {aud}      "patients" for a clinic or practice, otherwise "clients"
 *   {Aud}      the same, capitalised
 *   {owner}    the owner's first name
 *   {firms}    "practices" for a clinic, otherwise "firms"
 */

/** Which group a service belongs to, for picking copy. */
export const GROUP_OF_SERVICE = {
  'positioning-messaging': 'strategy',
  'brand-architecture': 'strategy',
  'visual-identity-system': 'identity',
  'brand-voice-guidelines': 'identity',
  'templates-rollout': 'identity',
  'website-design': 'website',
  'crm-marketing-automation': 'growth',
  'funnels-campaigns': 'growth',
  'fractional-cmo': 'leadership',
  'brand-system-maintenance': 'leadership',
}

/** Programs on the price list, as groups, for a service the map does not name. */
export const GROUP_OF_PROGRAM = {
  'brand-strategy': 'strategy',
  'brand-identity': 'identity',
  'website-growth': 'website',
  'marketing-leadership': 'leadership',
}

/** The order groups are read in: the order the work happens. */
export const GROUP_ORDER = ['strategy', 'identity', 'website', 'growth', 'leadership']

/** Words in a lead's request or a company name that point to a group. */
export const GROUP_WORDS = {
  strategy: /position|messag|strategy|naming|architecture|rebrand/i,
  identity: /identity|logo|visual|guideline|brand book|rebrand|branding/i,
  website: /web ?site|website|web design|landing page/i,
  growth: /crm|funnel|campaign|automation|email marketing|lead gen/i,
  leadership: /cmo|marketing lead|marketing director|retainer/i,
}

/** A clinic, practice or other care provider: its people are patients. */
export const PATIENT_WORDS = /\b(clinic|medical|medicine|health ?care|health|dental|dentist|dentistry|orthodont\w*|dermatolog\w*|chiropract\w*|physio\w*|physical therapy|therapy|therapist|wellness|hospital|med ?spa|pediatric\w*|surgery|surgical|surgeon\w*|optometr\w*|ophthalm\w*|cardio\w*|oncolog\w*|urgent care|family practice|primary care|dds)\b/i

export const CHALLENGES = {
  strategy: [
    ['Hard to explain in a sentence', 'Ask three people on your team what makes {company} different and you may get three answers. {Aud} hear the same mix, which makes the choice harder than it should be.'],
    ['Sounding like everyone else', 'From the outside, most {firms} in your field describe themselves in the same words. When the words match, {aud} decide on price or convenience instead.'],
    ['The business has outgrown its story', 'What you offer has grown since the brand was set. The way you describe it has not caught up.'],
  ],
  identity: [
    ['A look that trails the work', 'The quality of what you do is ahead of how the brand looks, and {aud} judge the first impression before they meet you.'],
    ['Different from place to place', 'The logo, colors and tone change from the website to print to social posts, so the brand never adds up to one thing.'],
    ['Everything waits for one person', 'Without clear guidelines, every new piece needs a senior eye before it can go out.'],
  ],
  website: [
    ['The website does not do the selling', 'Visitors arrive, look around and leave without getting in touch. The site describes {company} but does not make the case.'],
    ['Hard to find what matters', 'The pages that answer a visitor’s real questions are buried, thin or missing.'],
    ['Slow to keep current', 'Changing a page takes more time than it should, so the site falls behind the business.'],
  ],
  growth: [
    ['Leads slip through the cracks', 'Enquiries arrive by phone, form and email, and follow-up depends on someone remembering.'],
    ['No clear view of the pipeline', 'It is hard to see which marketing brings in {aud} and which only costs money.'],
    ['Every campaign starts from scratch', 'Campaigns are built by hand each time, so they happen less often than they should.'],
  ],
  leadership: [
    ['Nobody owns marketing', 'Marketing gets done between other jobs, so it starts and stops.'],
    ['Activity without a plan', 'There is plenty happening, but no single plan ties it to the goals for the year.'],
    ['The brand drifts after launch', 'Without someone looking after it, new pieces slowly move away from the system.'],
  ],
}

export const SUCCESS = {
  strategy: [
    ['One clear position', 'Everyone on the team explains {company} the same way, in a sentence.'],
    ['Messages that do the work', 'Pillars and proof points ready for the site, sales calls and campaigns.'],
    ['Easier decisions', '{Aud} see quickly why you are the right choice.'],
  ],
  identity: [
    ['Recognisable everywhere', 'An identity that reads as {company} on every screen and surface.'],
    ['Consistent without effort', 'Guidelines and templates your team can use on their own.'],
    ['A first impression that fits', 'The brand looks as good as the work behind it.'],
  ],
  website: [
    ['A site that makes the case', 'Clear pages that show {aud} why to choose you and what to do next.'],
    ['More enquiries from the same visits', 'Every page leads somewhere: a call, a form or a booking.'],
    ['Easy to run', 'A site your team can update without waiting on a developer.'],
  ],
  growth: [
    ['Every enquiry answered', 'Automated follow-up, so no lead waits for a reply.'],
    ['A pipeline you can read', 'One place that shows where {aud} come from and where they stall.'],
    ['Campaigns that keep running', 'Funnels that keep working long after launch.'],
  ],
  leadership: [
    ['Marketing with an owner', 'Senior leadership that owns the plan and reports on it every month.'],
    ['Effort tied to goals', 'Each month’s work traced back to what the business needs.'],
    ['A brand that stays current', 'The system kept up to date as the business changes.'],
  ],
}

/** One sentence per group for the letter: why that part of the work is there. */
export const LETTER_WHY = {
  strategy: 'Strategy comes first, because every later decision is easier once we agree who {company} is for and why {aud} should choose you.',
  identity: 'The identity then gives that position a look and a voice your team can use without me in the room.',
  website: 'The website puts it to work, with pages written to turn visitors into {aud}.',
  growth: 'The CRM and campaigns make sure every enquiry gets an answer and nothing depends on memory.',
  leadership: 'Each month I lead the marketing with you, so the work keeps moving after launch.',
}

/** "What you get" on the at-a-glance page, one line per service. */
export const GLANCE_LINE = {
  'positioning-messaging': 'Positioning and messaging your team can repeat',
  'brand-architecture': 'A clear structure for your services and names',
  'visual-identity-system': 'A visual identity, from logo to full system',
  'brand-voice-guidelines': 'A brand voice and guidelines anyone can follow',
  'templates-rollout': 'Templates that put the brand to work from day one',
  'website-design': 'A website built to turn visitors into {aud}',
  'crm-marketing-automation': 'A CRM that follows up with every lead',
  'funnels-campaigns': 'Funnels and campaigns that bring in enquiries',
  'fractional-cmo': 'Senior marketing leadership every month',
  'brand-system-maintenance': 'Ongoing care for the brand system',
}

/** What pads the glance list to four when fewer services are sold. */
export const GLANCE_PAD = ['Your own project portal', 'Two revision rounds per deliverable', 'Weekly updates from {owner}', 'Full ownership of the final work']

/** The five stages: name, what happens, the client's time. */
export const STAGES = [
  ['Discovery', 'We talk to you, your team and your {aud}, and study who you compete with. We find the words that already work.', 'a half-day kickoff and one interview'],
  ['Strategy', 'Who you serve, why you win and how to say it, on one page you can read in two minutes.', 'a 60-minute readout'],
  ['Design', 'The work itself, shown on real things: your site, your documents, your space.', 'a 90-minute presentation'],
  ['Revisions', 'Two focused rounds, guided by a short feedback form so nothing gets lost.', 'two feedback rounds, 5 business days each'],
  ['Delivery', 'Every file, a guide, and a handover session so your team uses it well from day one.', 'a 60-minute handover'],
]

/** Strategy, when the proposal sells no strategy service. */
export const STAGE_STRATEGY_EXISTING = 'We confirm who you serve and how to say it, building on the positioning you already have.'

/** What each service has a contractor do, for "On this project". */
export const CONTRACTOR_TASK = {
  'positioning-messaging': 'Helps shape the messaging',
  'brand-architecture': 'Supports the naming and structure work',
  'visual-identity-system': 'Works on the identity design with {owner}',
  'brand-voice-guidelines': 'Produces the brand guidelines',
  'templates-rollout': 'Builds the templates',
  'website-design': 'Builds the website',
  'crm-marketing-automation': 'Sets up the CRM and automations',
  'funnels-campaigns': 'Builds the funnels and campaigns',
  'fractional-cmo': 'Supports the monthly marketing work',
  'brand-system-maintenance': 'Keeps the brand files current',
}

/** A contractor's specialty, read as the services they can work on. */
export const SPECIALTY_SERVICES = [
  [/brand|identity|logo|graphic|illustrat|motion|design/i, ['visual-identity-system', 'brand-voice-guidelines', 'templates-rollout', 'brand-architecture']],
  [/web|develop|wordpress|webflow|shopify|front.?end|\bux\b|\bui\b/i, ['website-design']],
  [/crm|gohighlevel|high ?level|\bghl\b|hubspot|automation|funnel|email/i, ['crm-marketing-automation', 'funnels-campaigns']],
  [/marketing|\bads\b|seo|content|social|campaign/i, ['funnels-campaigns', 'fractional-cmo']],
  [/copy|writ/i, ['positioning-messaging', 'brand-voice-guidelines', 'website-design']],
]
